import { useState, useRef, MouseEvent } from 'react';
import { useIsPreviewing } from '@builder.io/react';
import { DisclosureProps as Props } from '../types';
import style from '../Accordion.module.scss';
import { useColorProvider } from '@/utils/ColorProvider';
import RichText from '@/components/RichText';
import { cls } from '@/utils/cls';
export default function Accordion({
  extendStyles,
  question,
  answer
}: Props) {
  const _className = cls(style.tab, extendStyles);
  const isPreviewing = useIsPreviewing();
  const {
    colors
  } = useColorProvider();
  const details = useRef<HTMLDetailsElement>(null);
  const summary = useRef<HTMLDivElement>(null);
  const content = useRef<HTMLDivElement>(null);
  // because the padding is on details, margin on summary we need to account for it.
  // Update this if adjusting detail padding
  const detailsBorders = 40;
  const summaryMargin = 20;
  let animation: Animation | undefined;
  const animOptions = {
    duration: 400,
    easing: 'ease-in-out'
  };
  const [isClosing, setIsClosing] = useState(false);
  const [isExpanding, setIsExpanding] = useState(false);
  const onAnimationFinish = (open: boolean) => {
    if (details.current) details.current.open = open;
    animation = undefined;
    setIsClosing(false);
    setIsExpanding(false);
    if (details.current) {
      details.current.style.height = details.current.style.overflow = '';
    }
  };
  const expand = () => {
    setIsExpanding(true);
    const startHeight = `${details.current?.offsetHeight}px`;
    const endHeight = `${(summary.current?.offsetHeight || 100) + (content.current?.offsetHeight || 100) + detailsBorders + summaryMargin}px`;
    if (animation) {
      animation.cancel();
    }
    animation = details.current?.animate({
      height: [startHeight, endHeight]
    }, animOptions);
    if (animation) animation.onfinish = () => onAnimationFinish(true);
    if (animation) animation.oncancel = () => setIsExpanding(false);
  };
  const open = () => {
    if (details.current) {
      details.current.style.height = `${details.current.offsetHeight}px`;
      details.current.open = true;
      window.requestAnimationFrame(() => {
        expand();
      });
    }
  };
  const shrink = () => {
    setIsClosing(true);
    const startHeight = `${details.current?.offsetHeight}px`;
    const endHeight = `${(summary.current?.offsetHeight || 30) + detailsBorders}px`;
    if (animation) {
      animation.cancel();
    }
    animation = details.current?.animate({
      height: [startHeight, endHeight]
    }, animOptions);
    if (animation) animation.onfinish = () => onAnimationFinish(false);
    if (animation) animation.oncancel = () => setIsExpanding(true);
  };
  const toggleSummary = (e: MouseEvent<HTMLDetailsElement>) => {
    e.preventDefault();
    if (details.current) details.current.style.overflow = 'hidden';
    if (isClosing || !details.current?.open) {
      open();
    } else if (isExpanding || details.current.open) {
      shrink();
    }
  };
  return <details className={_className} open={isPreviewing} onClick={e => toggleSummary(e)} ref={details} data-sentry-component="Accordion" data-sentry-source-file="Disclosure.tsx">
			<summary className={style.summary} ref={summary}>
				<h3 className={style.question}>{question}</h3>
				<svg viewBox="0 0 24 24" data-sentry-element="svg" data-sentry-source-file="Disclosure.tsx">
					<path d="M22 12L2 12" fill="none" strokeWidth="4" stroke={colors.primaryAccentColor || '#000000'} strokeLinecap="round" strokeLinejoin="round" data-sentry-element="path" data-sentry-source-file="Disclosure.tsx" />
					<path d="M12 2L12 22" fill="none" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" stroke={colors.primaryAccentColor || '#000000'} className={style.plus} data-sentry-element="path" data-sentry-source-file="Disclosure.tsx" />
				</svg>
			</summary>
			<RichText text={answer || ''} className={style.detail} ref={content} data-sentry-element="RichText" data-sentry-source-file="Disclosure.tsx" />
		</details>;
}